import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { authenticatedFetch, getUser } from '@/lib/auth';
import DpaSignatorySelector, { SignatorySelection } from './DpaSignatorySelector';
import { useConfirm } from './ui';
import {
  Shield, FileText, CheckCircle2, AlertTriangle, Loader2,
  Smartphone, Users, Download, Send, ArrowRight, Info, MailCheck
} from 'lucide-react';

interface Signatory {
  fullNavn: string;
  fornavn: string;
  etternavn: string;
  fodselsdato: string | null;
  rolleType: string;
  rolleGruppeType: string;
  rolleBeskrivelse: string;
  erFratraadt: boolean;
}

interface PendingGroup {
  id: string;
  expires_at: string;
  created_at: string;
  created_by_user_id: string;
  total: number;
  signed_count: number;
  signatories: Array<{
    full_name: string;
    role: string;
    email: string | null;
    is_initiator: boolean;
    signed: boolean;
  }>;
}

interface PrepareResponse {
  success: boolean;
  template: {
    dpa_type: string;
    version: string;
    template_name: string;
    content: string;
    is_draft: boolean;
    subprocessors: any[];
  };
  organization: {
    orgnr: string;
    name: string;
    signatories: Signatory[];
    from_cache: boolean;
  };
  signatory_selection?: SignatorySelection;
  pending_group?: PendingGroup | null;
  vipps_required: boolean;
  vipps_purpose: string;
}

type Step = 'read' | 'select_signatories' | 'delegates_sent' | 'awaiting_signatures' | 'choose_person' | 'vipps_init' | 'vipps_polling' | 'brreg_check' | 'signing' | 'success' | 'delegate';

interface Props {
  dpaType: string;         // 'kompetanseforbundet_studieforbund' | 'kompetanseforbundet_medlem' | 'lms_addendum'
  partyType: string;       // 'studieforbund' | 'medlem' | 'medlem_lms_addendum'
  partyId: string;
  lmsType?: string;        // For LMS-addendum
  lmsIntegrationId?: string;
  onComplete?: (signatureId: string) => void;
}

async function downloadDpaPdf(signatureId: string) {
  try {
    const res = await authenticatedFetch(`/api/dpa/pdf/${signatureId}`);
    if (!res.ok) throw new Error(`Kunne ikke hente PDF: ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e: any) {
    alert('Kunne ikke laste ned PDF: ' + e.message);
  }
}

export default function DpaSigningPage({ dpaType, partyType, partyId, lmsType, lmsIntegrationId, onComplete }: Props) {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('read');
  // Scroll til topp av siden ved step-endring — unngår at bruker må scrolle
  // opp for å se den nye seksjonens overskrift (spesielt viktig når DPA-teksten
  // over er lang).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prepData, setPrepData] = useState<PrepareResponse | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  async function handleDownloadSignedPdf() {
    if (!signatureId || pdfDownloading) return;
    setPdfDownloading(true);
    try {
      await downloadDpaPdf(signatureId);
    } finally {
      setPdfDownloading(false);
    }
  }
  const [phoneNumber, setPhoneNumber] = useState('');
  const [, setCibaAuthReqId] = useState<string | null>(null);
  const [cibaMessage, setCibaMessage] = useState<string>('');
  const [vippsVerified, setVippsVerified] = useState<{full_name?: string; birth_date?: string} | null>(null);
  const [signatureId, setSignatureId] = useState<string | null>(null);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [delegateName, setDelegateName] = useState('');
  const [showFullContent, setShowFullContent] = useState(true);
  // Multi-sig state
  const [multiGroupId, setMultiGroupId] = useState<string | null>(null);
  const [delegateInvitations, setDelegateInvitations] = useState<Array<{email: string; name: string; role: string}>>([]);
  const [initiateSubmitting, setInitiateSubmitting] = useState(false);
  const currentUser = getUser();
  const pollingRef = useRef<any>(null);
  const abortedRef = useRef<boolean>(false);

  // Hent DPA-info + Brreg-signaturrett
  useEffect(() => {
    abortedRef.current = false;  // Reset ved re-mount/dep-endring (Fix #4)
    (async () => {
      try {
        setLoading(true);
        const res = await authenticatedFetch('/api/dpa/sign/prepare', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dpa_type: dpaType, party_type: partyType, party_id: partyId, lms_type: lmsType }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Kunne ikke forberede signering');
        setPrepData(data);
        // Hvis det finnes en aktiv signeringsrunde, hopp direkte til status-visning
        if (data.pending_group) {
          setStep('awaiting_signatures');
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      abortedRef.current = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [dpaType, partyType, partyId, lmsType]);

  const initiateVipps = useCallback(async () => {
    setError(null);
    setStep('vipps_init');
    try {
      const res = await authenticatedFetch('/api/admin/vipps-2fa/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber, purpose: 'dpa_signing' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke starte Vipps');
      setCibaAuthReqId(data.auth_req_id);
      setCibaMessage(data.message);
      setStep('vipps_polling');
      pollVipps(data.auth_req_id, data.poll_interval || 5);
    } catch (e: any) {
      setError(e.message);
      setStep('choose_person');
    }
  }, [phoneNumber]);

  // Bruker setTimeout-recursive istedenfor setInterval for å unngå overlappende polls
  const pollVipps = useCallback((authReqId: string, startIntervalSec: number) => {
    let currentInterval = startIntervalSec;  // muterbar for slowDown-compounding (Fix #14)
    const tick = async () => {
      if (abortedRef.current) return;
      try {
        const res = await authenticatedFetch(`/api/admin/vipps-2fa/status/${authReqId}`);
        const data = await res.json();
        if (abortedRef.current) return;
        if (data.status === 'pending') {
          if (data.slowDown) currentInterval = Math.min(currentInterval * 2, 60);  // cap på 60s
          pollingRef.current = setTimeout(tick, currentInterval * 1000);
          return;
        }
        pollingRef.current = null;
        if (data.status === 'complete' && data.purpose === 'dpa_signing') {
          setVippsVerified(data.display || null);
          finalize(authReqId);
        } else if (data.status === 'rejected') {
          setError('Du avviste Vipps-forespørselen');
          setStep('choose_person');
        } else if (data.status === 'expired') {
          setError('Vipps-forespørselen utløp — prøv igjen');
          setStep('choose_person');
        } else {
          setError(data.error || 'Vipps-verifisering feilet');
          setStep('choose_person');
        }
      } catch (e: any) {
        if (abortedRef.current) return;
        pollingRef.current = null;
        setError(e.message);
        setStep('choose_person');
      }
    };
    if (pollingRef.current) clearTimeout(pollingRef.current);
    pollingRef.current = setTimeout(tick, currentInterval * 1000);
  }, []);

  const finalize = useCallback(async (authReqId: string) => {
    setStep('brreg_check');
    try {
      const res = await authenticatedFetch('/api/dpa/sign/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dpa_type: dpaType,
          party_type: partyType,
          party_id: partyId,
          lms_type: lmsType,
          lms_integration_id: lmsIntegrationId,
          auth_req_id: authReqId,
          signature_completion_group_id: multiGroupId || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.suggest_delegate) {
          setError(`${data.error} (Brreg-status: ${data.brreg_status}). Du kan sende signeringslenke til en person med registrert signaturrett i stedet.`);
          setStep('delegate');
          return;
        }
        throw new Error(data.error || 'Signering feilet');
      }
      setSignatureId(data.signature.id);
      setStep('success');
      if (onComplete) onComplete(data.signature.id);
    } catch (e: any) {
      setError(e.message);
      setStep('choose_person');
    }
  }, [dpaType, partyType, partyId, lmsType, lmsIntegrationId, onComplete, multiGroupId]);

  // Multi-sig: kanseller pågående signeringsrunde
  const cancelMultiSig = useCallback(async (reason?: string) => {
    if (!prepData?.pending_group) return;
    const ok = await confirm({
      title: 'Avbryte signeringsrunden?',
      message: 'Alle signeringslenker som er sendt ut vil bli ugyldige. En ny runde må startes på nytt.',
      variant: 'danger',
      confirmLabel: 'Avbryt signering',
      cancelLabel: 'Fortsett signering',
    });
    if (!ok) return;
    try {
      const res = await authenticatedFetch(`/api/dpa/sign/multi/cancel/${prepData.pending_group.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || 'Avbrutt av administrator' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke avbryte');
      // Refresh: gå tilbake til /onboarding
      navigate('/onboarding');
    } catch (e: any) {
      setError(e.message);
    }
  }, [prepData, navigate, confirm]);

  // Multi-sig: opprett gruppe + send delegat-e-post + evt. fortsett med Vipps
  const initiateMultiSig = useCallback(async (payload: {
    signatories: any[];
    authority_declaration_accepted: true;
    initiator_is_signer: boolean;
  }) => {
    setError(null);
    setInitiateSubmitting(true);
    try {
      const res = await authenticatedFetch('/api/dpa/sign/multi/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dpa_type: dpaType,
          party_type: partyType,
          party_id: partyId,
          lms_type: lmsType,
          lms_integration_id: lmsIntegrationId,
          ...payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke starte multi-sig');
      setMultiGroupId(data.group_id);
      setDelegateInvitations(data.delegate_invitations || []);
      if (data.initiator_signature?.proceed_with_vipps) {
        // Initiator signerer nå — gå til Vipps-init
        setStep('choose_person');
      } else {
        // Kun delegater — vis kvittering
        setStep('delegates_sent');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInitiateSubmitting(false);
    }
  }, [dpaType, partyType, partyId, lmsType, lmsIntegrationId]);

  const sendDelegate = useCallback(async () => {
    setError(null);
    try {
      const res = await authenticatedFetch('/api/dpa/sign/delegate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dpa_type: dpaType,
          party_type: partyType,
          party_id: partyId,
          delegate_email: delegateEmail,
          delegate_name: delegateName || null
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert(`Signeringslenke sendt til ${delegateEmail}. Lenken er gyldig i 7 dager.`);
      navigate('/dashboard');
    } catch (e: any) {
      setError(e.message);
    }
  }, [dpaType, partyType, partyId, delegateEmail, delegateName, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (error && !prepData) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Kunne ikke laste signeringssiden</h3>
              <p className="text-sm text-red-800 mt-1">{error}</p>
              <button onClick={() => navigate('/dashboard')} className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Tilbake til dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!prepData) return null;

  const isDraft = prepData.template.is_draft;
  const isLmsAddendum = dpaType === 'lms_addendum';

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-6 w-6 text-kf-primary" />
              <h1 className="text-2xl font-bold text-kf-primary">Databehandleravtale</h1>
            </div>
            <p className="text-gray-600">{prepData.template.template_name}</p>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <span className="px-2 py-1 bg-kf-blue-50 text-kf-blue-800 rounded">Versjon {prepData.template.version}</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">Org: {prepData.organization.name || prepData.organization.orgnr}</span>
              {isLmsAddendum && lmsType && <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">LMS: {lmsType}</span>}
            </div>
          </div>
        </div>

        {isDraft && (
          <div className="mt-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-700 flex-shrink-0" />
              <div className="text-sm">
                <strong className="text-yellow-900">UTKAST — IKKE JURIDISK BINDENDE</strong>
                <p className="text-yellow-800 mt-1">
                  Denne signeringen er utført med utkast-mal under juridisk gjennomgang.
                  Prod-versjonen publiseres etter juridisk godkjenning og krever da ny signering.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Steg-avhengig innhold */}
      {step === 'select_signatories' && prepData.signatory_selection && (
        <DpaSignatorySelector
          selection={prepData.signatory_selection}
          currentUser={{ full_name: currentUser?.full_name || '', email: currentUser?.email || '' }}
          organizationName={prepData.organization.name || prepData.organization.orgnr}
          onCancel={() => setStep('read')}
          onSubmit={initiateMultiSig}
          submitting={initiateSubmitting}
          externalError={error}
        />
      )}

      {step === 'awaiting_signatures' && prepData.pending_group && (
        <div className="bg-white rounded-lg shadow-sm border border-kf-blue-200 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-8 w-8 text-kf-blue-600 animate-pulse" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Signeringsrunde pågår</h2>
              <p className="text-sm text-gray-600">
                {prepData.pending_group.signed_count} av {prepData.pending_group.total} har signert
              </p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
            Systemet er låst for {prepData.organization.name || 'organisasjonen'} inntil databehandleravtalen er signert av alle valgte personer.
            Andre funksjoner blir tilgjengelige når signeringen er fullført.
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Ventet signering fra:</div>
            <ul className="border border-gray-200 rounded-md divide-y divide-gray-100">
              {prepData.pending_group.signatories.map((s, i) => (
                <li key={i} className="p-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{s.full_name}</div>
                    <div className="text-xs text-gray-500">
                      {s.email || '(e-post mangler)'} — {s.role}
                      {s.is_initiator && ' — initiator'}
                    </div>
                  </div>
                  {s.signed ? (
                    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Signert
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">Venter</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-gray-500">
            Signeringslenkene er gyldige til {new Date(prepData.pending_group.expires_at).toLocaleDateString('no-NO')}.
            Har du valgt feil signatarer eller trenger å legge til/fjerne noen? Avbryt og start ny runde.
          </p>
          <div className="flex gap-3 border-t border-gray-100 pt-4">
            <button onClick={() => navigate('/onboarding')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
              Tilbake til oversikten
            </button>
            <button onClick={() => cancelMultiSig()}
              className="px-4 py-2 border-2 border-red-300 text-red-700 rounded-md hover:bg-red-50 inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Avbryt signeringsrunde og start på nytt
            </button>
          </div>
          {error && (
            <div className="border border-red-300 bg-red-50 rounded-md p-3 text-sm text-red-800">{error}</div>
          )}
        </div>
      )}

      {step === 'delegates_sent' && (
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <MailCheck className="h-8 w-8 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Signeringslenker sendt</h2>
          </div>
          <p className="text-sm text-gray-700">
            Vi har sendt e-post med signeringslenke til <strong>{delegateInvitations.length}</strong> {delegateInvitations.length === 1 ? 'signatar' : 'signatarer'}.
            Avtalen blir aktiv når alle har fullført Vipps-signering.
          </p>
          <ul className="border border-gray-200 rounded-md divide-y divide-gray-100">
            {delegateInvitations.map((d, i) => (
              <li key={i} className="p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{d.name}</div>
                  <div className="text-xs text-gray-500">{d.email} — {d.role}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">Venter på signering</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            Lenkene er gyldige i 7 dager. Signeringsstatus finner du under Databehandleravtale-oversikten i Innstillinger.
          </p>
          <button onClick={() => navigate('/onboarding')}
            className="px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark">
            Tilbake til oversikten
          </button>
        </div>
      )}

      {(step === 'read' || step === 'choose_person') && (
        <>
          {/* DPA-innhold */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-5 w-5" /> Avtaleinnhold
              </h2>
              <button onClick={() => setShowFullContent(!showFullContent)} className="text-sm text-kf-primary hover:underline">
                {showFullContent ? 'Skjul' : 'Vis'} full tekst
              </button>
            </div>
            {showFullContent ? (
              <div className="max-h-[70vh] overflow-y-auto bg-white border border-gray-200 rounded-md p-6 text-sm text-gray-800 leading-relaxed">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xl font-bold text-kf-primary mt-4 mb-3 pb-2 border-b border-gray-200">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-lg font-semibold text-kf-primary mt-5 mb-2">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-base font-semibold text-gray-900 mt-4 mb-2 border-l-4 border-kf-primary pl-3">{children}</h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1 uppercase tracking-wide">{children}</h4>
                    ),
                    p: ({ children }) => <p className="mb-3">{children}</p>,
                    ol: ({ children }) => <ol className="list-decimal ml-6 mb-3 space-y-1.5">{children}</ol>,
                    ul: ({ children }) => <ul className="list-disc ml-6 mb-3 space-y-1.5">{children}</ul>,
                    li: ({ children }) => <li className="pl-1">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    code: ({ children }) => (
                      <code className="bg-gray-100 text-kf-primary px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-kf-primary/40 bg-gray-50 pl-4 py-2 my-3 italic text-gray-700">{children}</blockquote>
                    ),
                    hr: () => <hr className="my-4 border-gray-200" />,
                    a: ({ children, href }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-kf-primary underline hover:no-underline">{children}</a>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-3">
                        <table className="min-w-full border-collapse border border-gray-300 text-sm">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
                    th: ({ children }) => (
                      <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-900">{children}</th>
                    ),
                    td: ({ children }) => <td className="border border-gray-300 px-3 py-2">{children}</td>,
                  }}
                >
                  {prepData.template.content}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Klikk "Vis full tekst" for å lese hele avtalen (~{Math.round(prepData.template.content.length / 100) / 10}k tegn).
                Innholdet dekker rolledefinisjon (behandlingsansvarlig/databehandler), personopplysningskategorier,
                sikkerhetstiltak, underdatabehandlere, sikkerhetsbrudd-varsling og ansvarsforhold etter GDPR.
              </p>
            )}

            {/* Underdatabehandlere som quick-list */}
            {prepData.template.subprocessors && prepData.template.subprocessors.length > 0 && (
              <div className="mt-4 p-3 bg-kf-blue-50 rounded border border-kf-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4 text-kf-blue-700" />
                  <strong className="text-sm text-kf-blue-900">Underdatabehandlere</strong>
                </div>
                <ul className="text-xs text-kf-blue-800 space-y-1">
                  {prepData.template.subprocessors.map((sp: any, i: number) => (
                    <li key={i}>• <strong>{sp.name}</strong> ({sp.country}) — {sp.purpose}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Signeringsrett-info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Users className="h-5 w-5" /> Personer med signaturrett i Brønnøysundregisteret
            </h2>
            {prepData.organization.signatories && prepData.organization.signatories.length > 0 ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Følgende personer er registrert med rolle som gir signaturrett for <strong>{prepData.organization.name}</strong> (org.nr. {prepData.organization.orgnr}):
                </p>
                <ul className="space-y-2">
                  {prepData.organization.signatories.slice(0, 10).map((s, i) => (
                    <li key={i} className="text-sm bg-gray-50 rounded px-3 py-2 flex items-center justify-between">
                      <span>
                        <strong>{s.fullNavn}</strong>
                        <span className="text-gray-500 ml-2">({s.rolleBeskrivelse})</span>
                      </span>
                      {s.fodselsdato && <span className="text-xs text-gray-400">f. {s.fodselsdato}</span>}
                    </li>
                  ))}
                </ul>
                {prepData.organization.signatories.length > 10 && (
                  <p className="text-xs text-gray-500 mt-2">+ {prepData.organization.signatories.length - 10} flere</p>
                )}
              </>
            ) : (
              <p className="text-sm text-yellow-800 bg-yellow-50 p-3 rounded border border-yellow-200">
                Ingen personer funnet med signaturrett i Brønnøysund. Sjekk at organisasjonsnummeret er registrert korrekt.
              </p>
            )}
          </div>

          {/* Ved 'read': fortsett til signatarvalg. Ved 'choose_person' (etter multi-init): Vipps-init for initiator */}
          {step === 'read' ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Users className="h-5 w-5" /> Velg signatarer og signer
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Neste steg lar deg velge hvem som skal signere avtalen på vegne av organisasjonen.
                Signaturblokken i kontrakten oppdateres automatisk basert på valgene dine.
              </p>
              <button
                onClick={() => setStep('select_signatories')}
                disabled={!prepData.signatory_selection}
                className="px-6 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark inline-flex items-center gap-2 disabled:opacity-50"
              >
                Fortsett til signatarvalg <ArrowRight className="h-4 w-4" />
              </button>
              {error && <p className="text-sm text-red-700 mt-3">{error}</p>}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Kun én person med signaturrett?</strong> Signeringslenke kan sendes til en person med
                  registrert signaturrett i Brønnøysund som fullfører alene.
                </p>
                <button onClick={() => setStep('delegate')} className="text-sm text-kf-primary hover:underline flex items-center gap-1">
                  <Send className="h-4 w-4" /> Send signeringslenke til én person
                </button>
              </div>
            </div>
          ) : (
            /* choose_person — initiator fortsetter med Vipps etter multi-init */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Smartphone className="h-5 w-5" /> Din Vipps-signering
              </h2>
              {multiGroupId && delegateInvitations.length > 0 && (
                <div className="mb-4 p-3 rounded bg-kf-blue-50 border border-kf-blue-200 text-sm text-kf-blue-900">
                  Signeringsrunde opprettet. Delegat-invitasjon sendt til {delegateInvitations.length} {delegateInvitations.length === 1 ? 'person' : 'personer'} —
                  du signerer nå selv, avtalen blir aktiv når alle er ferdig.
                </div>
              )}
              <p className="text-sm text-gray-600 mb-4">
                Skriv inn ditt mobilnummer for Vipps-verifisering.
              </p>
              <div className="flex gap-3">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+47 xxx xx xxx"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-kf-primary focus:border-kf-primary"
                />
                <button
                  onClick={initiateVipps}
                  disabled={!phoneNumber}
                  className="px-6 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-primary-dark disabled:opacity-50 flex items-center gap-2"
                >
                  Start Vipps <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              {error && <p className="text-sm text-red-700 mt-3">{error}</p>}
            </div>
          )}
        </>
      )}

      {(step === 'vipps_init' || step === 'vipps_polling' || step === 'brreg_check') && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Loader2 className="h-12 w-12 text-kf-primary mx-auto animate-spin mb-4" />
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'vipps_init' && 'Starter Vipps-forespørsel...'}
            {step === 'vipps_polling' && 'Venter på bekreftelse i Vipps-appen'}
            {step === 'brreg_check' && 'Verifiserer signaturrett mot Brønnøysund og signerer PDF...'}
          </h2>
          {cibaMessage && <p className="text-sm text-gray-600 mt-2">{cibaMessage}</p>}
          <p className="text-xs text-gray-500 mt-4">Ikke lukk vinduet — flyten fullføres automatisk.</p>
        </div>
      )}

      {step === 'success' && (
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-8">
          <div className="text-center mb-4">
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-900 mt-3">Databehandleravtalen er signert</h2>
            {isDraft && <p className="text-sm text-yellow-800 mt-2 font-medium">Merk: signeringen er utført med UTKAST-mal. Endelig versjon krever ny signering etter juridisk gjennomgang.</p>}
          </div>
          <div className="text-sm text-gray-700 space-y-2 max-w-md mx-auto">
            <p>✓ Vipps-verifisert identitet: <strong>{vippsVerified?.full_name}</strong></p>
            <p>✓ Signaturrett bekreftet i Brønnøysundregisteret</p>
            <p>✓ PDF signert med Buypass eSeal og lagret</p>
            <p>✓ E-post-kvittering med PDF sendt til alle relevante parter</p>
          </div>
          <div className="mt-6 flex gap-3 justify-center">
            {signatureId && (
              <button
                onClick={handleDownloadSignedPdf}
                disabled={pdfDownloading}
                className="px-4 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
                title={pdfDownloading ? 'Henter og genererer signert PDF …' : 'Last ned signert PDF'}
              >
                {pdfDownloading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Henter signert PDF …
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" /> Last ned signert PDF
                  </>
                )}
              </button>
            )}
            <button onClick={() => navigate('/onboarding')} className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              Fortsett onboarding
            </button>
          </div>
        </div>
      )}

      {step === 'delegate' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Send className="h-5 w-5" /> Send signeringslenke til person med signaturrett
          </h2>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>}
          <p className="text-sm text-gray-600 mb-4">
            Send en engangs-signeringslenke til en person som har signaturrett for organisasjonen.
            Lenken er gyldig i 7 dager. Personen må bruke Vipps for å fullføre signeringen.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-postadresse *</label>
              <input type="email" value={delegateEmail} onChange={(e) => setDelegateEmail(e.target.value)}
                     placeholder="daglig.leder@organisasjon.no"
                     className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-kf-primary focus:border-kf-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Navn (valgfritt)</label>
              <input type="text" value={delegateName} onChange={(e) => setDelegateName(e.target.value)}
                     placeholder="Ola Nordmann"
                     className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-kf-primary focus:border-kf-primary" />
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={sendDelegate} disabled={!delegateEmail}
                    className="px-6 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark disabled:opacity-50 flex items-center gap-2">
              <Send className="h-4 w-4" /> Send lenke
            </button>
            <button onClick={() => { setStep('read'); setError(null); }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              Avbryt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
